import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import Promise from 'bluebird';
import { Select } from '@bingads-webui-react/primitive';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { LineByLineFormGroup, InputFormGroup } from '@bingads-webui-cc-react/layout';
import { AddressForm } from './address-form';

const { CountryCode } = Constants;
const { inlineFormat } = AddressUtils;
const newAddressId = -1;
const mapAddressViewList = (addressItems, bypassId) => _.map(addressItems, (item, index) => ({
  id: bypassId ? index : item.Id,
  value: inlineFormat(item),
}));
const renderOptions = optionList => optionList.map(({ id, value }) => <option key={id} value={id}>{value}</option>);
const trimValidationAddress = validationAddress => _.chain(validationAddress)
  .omit('isValid')
  .mapObject(({ value }) => value)
  .value();

const EmptyFormGroup = ({ children }) => <React.Fragment>{ children }</React.Fragment>;
EmptyFormGroup.propTypes = {
  children: PropTypes.node.isRequired,
};

const getCustomizedFormGroup = (showSectionLabel, showLineByLineFormGroup) => {
  if (showSectionLabel) {
    return showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  }
  return EmptyFormGroup;
};

export class CustomerAddressSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = { addressViewList: null, selectedAddressId: null };
    this.CustomizedFormGroup = getCustomizedFormGroup(props.showSectionLabel, props.showLineByLineFormGroup);
  }

  componentDidMount() {
    if (_.isEmpty(this.props.addressList)) {
      this.findCustomerAddressCallToken = createAsyncToken(this.props.findCustomerAddress())
        .on({ onSuccess: addressList => this.setAddressViewList(addressList) });
    } else {
      this.setAddressViewList(this.props.addressList);
    }
  }

  componentWillUnmount() {
    if (this.findCustomerAddressCallToken) {
      this.findCustomerAddressCallToken.cancel();
    }
  }

  setAddressViewList(addressList) {
    const addressViewList = mapAddressViewList(addressList, this.props.bypassId);
    addressViewList.push({ id: newAddressId, value: this.props.i18n.getString(_TL_('Enter a new address')) });
    this.setState({
      addressList,
      addressViewList,
    });
  }

  getSelectedAddress() {
    if (this.isManagedByProps()) {
      return this.props.selectedAddress;
    }
    const id = this.state.selectedAddressId === null ? _.result(_.first(this.state.addressViewList), 'id') : this.state.selectedAddressId;
    return this.getAddressFromAddressList(id);
  }

  getAddressFromAddressList(id) {
    const { addressList } = this.state;
    return {
      ...(this.props.bypassId ? _.result(addressList, id, {}) : _.findWhere(addressList, { Id: id })),
      Id: id,
    };
  }

  handleAddressSelect = (id) => {
    if (this.isManagedByProps()) {
      this.props.onAddressChange({ addressWithValidation: { isValid: false }, addressObject: this.getAddressFromAddressList(id) });
    } else {
      this.setState({ selectedAddressId: id });
    }
  };

  handleAddressChange = (addressWithValidation) => {
    this.props.onAddressChange({
      addressWithValidation,
      addressObject: {
        Id: this.getSelectedAddress().Id,
        ...trimValidationAddress(addressWithValidation),
      },
    });
  };

  isManagedByProps() {
    return !_.isEmpty(this.props.selectedAddress);
  }

  renderAddressSelector = (selectedAddressId, isDisabled) =>
    (isDisabled ? this.renderDisabledAddressSelector() : this.renderFullAddressSelector(selectedAddressId));

  renderDisabledAddressSelector = () => (
    <Select disabled>
      <option>{inlineFormat(this.props.selectedAddress)}</option>
    </Select>
  );

  renderFullAddressSelector = (selectedAddressId) => {
    const { addressViewList } = this.state;
    const selectedItem = _.findWhere(addressViewList, { id: selectedAddressId });
    return (
      <Select
        value={selectedItem ? selectedItem.id : _.first(addressViewList).id}
        onChange={({
          target: { value: newValue },
        }) => this.handleAddressSelect(Number(newValue))}
      >
        {renderOptions(addressViewList)}
      </Select>);
  };

  render() {
    if (!this.state.addressViewList) {
      return null;
    }

    const {
      id,
      i18n,
      findCountries,
      market,
      countries,
      findStates,
      showLineByLineFormGroup,
      showSectionLabel,
      showFieldsLabel,
      showPlaceholder,
      disabled,
      addressSelectorLabel,
      reconstructionId,
      alwaysShowError,
    } = this.props;

    const {
      Id,
      ...selectedAddress
    } = this.getSelectedAddress();

    return (
      <div className="customer-address-selector">
        <div className="address-list">
          <this.CustomizedFormGroup
            label={addressSelectorLabel || this.props.i18n.getString(_TL_('Business address'))}
            helpPopupTopic="Pop_BA_Accounts_BusinessAddress"
          >
            {this.renderAddressSelector(Id, disabled)}
          </this.CustomizedFormGroup>
        </div>
        { !disabled && <AddressForm
          key={_.isNull(reconstructionId) ? Id : reconstructionId}
          id={id}
          i18n={i18n}
          findCountries={findCountries}
          market={market}
          countries={countries}
          findStates={findStates}
          onAddressChange={this.handleAddressChange}
          model={selectedAddress}
          showLineByLineFormGroup={showLineByLineFormGroup}
          showSectionLabel={showSectionLabel}
          showFieldsLabel={showFieldsLabel}
          showPlaceholder={showPlaceholder}
          alwaysShowError={alwaysShowError}
          useDropdownList
        /> }
      </div>
    );
  }
}

CustomerAddressSelector.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  market: PropTypes.string,
  bypassId: PropTypes.bool,
  selectedAddress: PropTypes.shape({
    Id: PropTypes.number,
    Country: PropTypes.string,
    StateOrProvince: PropTypes.string,
    City: PropTypes.string,
    PostalCode: PropTypes.string,
    Line1: PropTypes.string,
    Line2: PropTypes.string,
  }),
  addressList: PropTypes.arrayOf(PropTypes.object),
  findCountries: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  })),
  findStates: PropTypes.func.isRequired,
  findCustomerAddress: PropTypes.func,
  onAddressChange: PropTypes.func.isRequired,
  showLineByLineFormGroup: PropTypes.bool,
  showSectionLabel: PropTypes.bool,
  showFieldsLabel: PropTypes.bool,
  showPlaceholder: PropTypes.bool,
  disabled: PropTypes.bool,
  addressSelectorLabel: PropTypes.string,
  reconstructionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  alwaysShowError: PropTypes.bool,
};

CustomerAddressSelector.defaultProps = {
  market: CountryCode.US,
  bypassId: false,
  selectedAddress: null,
  addressList: [],
  countries: [],
  findCustomerAddress: () => Promise.resolve([]),
  showLineByLineFormGroup: false,
  showSectionLabel: false,
  showFieldsLabel: false,
  showPlaceholder: false,
  disabled: false,
  addressSelectorLabel: null,
  reconstructionId: null,
  alwaysShowError: false,
};
